import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { inr, chipGlow, FAMILIES, BATTERY_CONDITIONS } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowLeft, Share2, Download, MessageCircle, CheckCircle2, Clock, RotateCcw,
  Pencil, Trash2, Loader2, ShieldCheck, Battery,
} from "lucide-react";
import { toast } from "sonner";

export default function UnitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [cardBust, setCardBust] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/units/${id}`);
      setUnit(data);
      setForm(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!unit)
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2997ff]" /></div>;

  const glow = chipGlow(unit.chip);
  const cardUrl = `${API}/stock/${unit.stock_code}/card.png?v=${cardBust}`;

  const changeStatus = async (action, price) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/units/${unit.id}/status`, { action, price });
      setUnit(data); setForm(data); setCardBust(Date.now());
      toast.success("Status updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const markSold = async () => {
    const input = window.prompt("Final sold price (₹). Leave blank to keep current:", unit.price || "");
    if (input === null) return;
    const price = input.trim() === "" ? undefined : Number(input);
    changeStatus("sold", price);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {
        stock_code: form.stock_code, status: form.status,
        family: form.family, size: form.size, year: form.year ? Number(form.year) : null,
        chip: form.chip, cpu_note: form.cpu_note, memory: form.memory, storage: form.storage,
        display: form.display, macos: form.macos,
        battery_cycles: form.battery_cycles === "" || form.battery_cycles == null ? null : Number(form.battery_cycles),
        battery_capacity: form.battery_capacity === "" || form.battery_capacity == null ? null : Number(form.battery_capacity),
        battery_condition: form.battery_condition, colour: form.colour, serial: form.serial,
        purchase_cost: form.purchase_cost === "" || form.purchase_cost == null ? null : Number(form.purchase_cost),
        price: form.price === "" || form.price == null ? null : Number(form.price),
        notes: form.notes, real_photo: form.real_photo ?? null,
      };
      const { data } = await api.patch(`/units/${unit.id}`, payload);
      setUnit(data); setForm(data); setEditing(false); setCardBust(Date.now());
      toast.success("Saved — card, PDF & website updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const onEditPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, real_photo: r.result }));
    r.readAsDataURL(file);
  };

  const del = async () => {
    if (!window.confirm(`Delete ${unit.stock_code}? This cannot be undone.`)) return;
    try { await api.delete(`/units/${unit.id}`); toast.success("Deleted"); navigate("/"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadCard = async () => {
    try {
      const res = await api.get(`/stock/${unit.stock_code}/card.png`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `MacFreaks-${unit.stock_code}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const shareWhatsApp = async () => {
    const text = decodeURIComponent(unit.whatsapp_url.split("text=")[1] || "");
    try {
      const res = await api.get(`/stock/${unit.stock_code}/card.png`, { responseType: "blob" });
      const file = new File([res.data], `${unit.stock_code}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
        return;
      }
    } catch { /* fall through */ }
    window.open(unit.whatsapp_url, "_blank");
  };

  const input = "h-11 w-full rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]";
  const ef = (label, key, props = {}) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-[#98989d]">{label}</label>
      <input className={input} value={form[key] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} data-testid={`edit-${key}`} {...props} />
    </div>
  );

  return (
    <div className="mf-fade-in pb-4">
      <button onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-[#98989d]" data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Left: card image */}
        <div>
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <img key={cardBust} src={cardUrl} alt={unit.stock_code} className="w-full" data-testid="card-image" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid="share-whatsapp-button" onClick={shareWhatsApp} className="flex items-center justify-center gap-2 rounded-xl border border-[#30d158]/40 bg-[#30d158]/10 py-3 text-sm font-semibold text-[#30d158] active:scale-[0.98]">
              <Share2 size={16} /> Share to WhatsApp
            </button>
            <button data-testid="download-card-button" onClick={downloadCard} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#161618] py-3 text-sm font-semibold active:scale-[0.98]">
              <Download size={16} /> Download card
            </button>
          </div>
        </div>

        {/* Right: info + actions */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono text-sm text-[#98989d]">{unit.stock_code}</span>
            <StatusBadge status={unit.status} testid={`detail-status-${unit.stock_code}`} />
          </div>
          <div className="text-sm uppercase tracking-wide text-[#98989d]">{unit.family}</div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            {unit.size} <span style={{ color: glow }}>{unit.chip}</span>
          </h1>
          {unit.cpu_note && <p className="mt-1 text-sm text-[#98989d]">{unit.cpu_note}</p>}

          <div className="mt-2 text-2xl font-extrabold">
            {unit.price ? inr(unit.price) : <span className="text-base text-[#98989d]">Ask for price</span>}
          </div>

          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="flex items-center gap-1 rounded-full border border-white/10 bg-[#161618] px-3 py-1.5">
              <ShieldCheck size={13} className="text-[#2997ff]" /> {unit.warranty_days}-day warranty
            </span>
            <span className={`flex items-center gap-1 rounded-full border px-3 py-1.5 ${unit.battery_condition === "Normal" ? "border-white/10 bg-[#161618] text-white" : "border-[#ff9f0a]/40 bg-[#ff9f0a]/10 text-[#ff9f0a]"}`}>
              <Battery size={13} /> {unit.battery_capacity ? `${unit.battery_capacity}% · ` : ""}{unit.battery_cycles != null ? `${unit.battery_cycles} cyc · ` : ""}{unit.battery_condition}
            </span>
          </div>

          {/* spec grid */}
          <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 text-sm">
            <Row k="Memory" v={unit.memory} />
            <Row k="Storage" v={unit.storage} />
            <Row k="Display" v={unit.display} wide />
            <Row k="macOS" v={unit.macos} wide />
            <Row k="Colour" v={unit.colour || "—"} />
            <Row k="Serial" v={unit.serial || "—"} />
            <Row k="Added" v={(unit.date_added || "").slice(0, 10)} />
            <Row k="Sold" v={(unit.date_sold || "").slice(0, 10) || "—"} />
          </div>

          {/* status actions */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <button data-testid={`reserve-button-${unit.stock_code}`} disabled={busy} onClick={() => changeStatus("reserve")} className="flex flex-col items-center gap-1 rounded-xl border border-[#ff9f0a]/30 bg-[#ff9f0a]/10 py-3 text-xs font-semibold text-[#ff9f0a] active:scale-95">
              <Clock size={16} /> Reserve
            </button>
            <button data-testid={`mark-sold-button-${unit.stock_code}`} disabled={busy} onClick={markSold} className="flex flex-col items-center gap-1 rounded-xl border border-[#ff453a]/30 bg-[#ff453a]/10 py-3 text-xs font-semibold text-[#ff453a] active:scale-95">
              <CheckCircle2 size={16} /> Mark sold
            </button>
            <button data-testid={`back-in-stock-button-${unit.stock_code}`} disabled={busy} onClick={() => changeStatus("instock")} className="flex flex-col items-center gap-1 rounded-xl border border-[#30d158]/30 bg-[#30d158]/10 py-3 text-xs font-semibold text-[#30d158] active:scale-95">
              <RotateCcw size={16} /> Back in stock
            </button>
          </div>

          <a data-testid="detail-whatsapp-customer" href={unit.whatsapp_url} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#161618] py-3 text-sm font-semibold active:scale-[0.98]">
            <MessageCircle size={16} className="text-[#30d158]" /> Prefilled customer WhatsApp
          </a>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <button data-testid="edit-unit-button" onClick={() => setEditing((e) => !e)} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#161618] py-3 text-sm font-semibold active:scale-[0.98]">
              <Pencil size={15} /> {editing ? "Close edit" : "Edit"}
            </button>
            <button data-testid="delete-unit-button" onClick={del} className="flex items-center justify-center gap-2 rounded-xl border border-[#ff453a]/30 py-3 text-sm font-semibold text-[#ff453a] active:scale-[0.98]">
              <Trash2 size={15} /> Delete
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <div className="mf-fade-in mt-5 space-y-3 rounded-3xl border border-white/10 bg-[#121214] p-4">
          <h3 className="font-semibold">Edit unit — all fields</h3>
          <div className="grid grid-cols-2 gap-3">
            {ef("Stock code", "stock_code")}
            <div>
              <label className="mb-1 block text-xs font-medium text-[#98989d]">Status</label>
              <select className={input} value={form.status || "In stock"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="edit-status">
                {["In stock", "Reserved", "Sold", "Hidden"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[#98989d]">Family</label>
              <select className={input} value={form.family || ""} onChange={(e) => setForm((f) => ({ ...f, family: e.target.value }))} data-testid="edit-family">
                {FAMILIES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            {ef("Size", "size")}{ef("Year", "year", { type: "number" })}{ef("Chip", "chip")}
            {ef("CPU note", "cpu_note")}{ef("Memory", "memory")}{ef("Storage", "storage")}
            {ef("Display", "display")}{ef("macOS", "macos")}{ef("Cycles", "battery_cycles", { type: "number" })}
            {ef("Max cap %", "battery_capacity", { type: "number" })}
            <div>
              <label className="mb-1 block text-xs font-medium text-[#98989d]">Battery</label>
              <select className={input} value={form.battery_condition || "Normal"} onChange={(e) => setForm((f) => ({ ...f, battery_condition: e.target.value }))} data-testid="edit-battery_condition">
                {BATTERY_CONDITIONS.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            {ef("Colour", "colour")}{ef("Serial", "serial")}
            {ef("Purchase cost ₹", "purchase_cost", { type: "number" })}{ef("Price ₹", "price", { type: "number" })}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[#98989d]">Notes</label>
            <textarea className="min-h-20 w-full rounded-xl border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-[#2997ff]" value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="edit-notes" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[#98989d]">Real photo of this unit (overrides the studio photo on its card)</label>
            <div className="flex items-center gap-3">
              {form.real_photo ? (
                <img src={form.real_photo} alt="real" className="h-16 w-24 rounded-lg border border-white/10 object-contain" />
              ) : (
                <span className="text-xs text-[#98989d]">Using the model studio photo</span>
              )}
              <label className="cursor-pointer rounded-xl border border-white/10 bg-[#161618] px-3 py-2 text-xs font-semibold">
                Upload photo
                <input type="file" accept="image/*" className="hidden" onChange={onEditPhoto} data-testid="edit-real-photo-input" />
              </label>
              {form.real_photo && (
                <button onClick={() => setForm((f) => ({ ...f, real_photo: null }))} className="text-xs font-semibold text-[#ff453a]" data-testid="edit-remove-photo">Remove</button>
              )}
            </div>
          </div>
          <button data-testid="save-edit-button" onClick={saveEdit} disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#2997ff] font-semibold active:scale-[0.98] disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, wide }) {
  return (
    <div className={`bg-[#121214] px-3 py-2.5 ${wide ? "col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-wide text-[#98989d]">{k}</div>
      <div className="font-medium">{v || "—"}</div>
    </div>
  );
}
